/**
 * A wrapper for CodeMirror
 * Gives styling and other functionality to text areas with code
 * https://codemirror.net/
 */
namespace appn.codeMirror {
    /**
     * This will apply CodeMirror to a text area.
     * Please view appn.views.messageText.edit as an example if you are struggling with linting.
     * 
     * textAreaSelector preferably needs to be the "id" of the element related to a label when linting is enabled.
     */
    export function applyToTextArea(textAreaSelector: string, codeType: codeTypes): any {
        if (textAreaSelector.startsWithIgnoreCase('#'))         // ensure no ambiguity in terms of target element
            textAreaSelector = "textarea" + textAreaSelector;

        if ($(textAreaSelector).length <= 0)
            return null;

        (window as any).currentCodeWindow = textAreaSelector;

        let config: any = null;

        switch (codeType) {
            case codeTypes.htmlAndScript: {
                config = getHtmlAndScriptConfig();
                break;
            }
            case codeTypes.scss: {
                config = getScssConfig();
                break;
            }
            case codeTypes.css: {
                config = getCssConfig();
                break;
            }
            case codeTypes.json: {
                config = getJsonConfig();
                break;
            }
            case codeTypes.formulaBuilder: {
                config = getFormulaBuilderConfig();
                break;
            }
        }

        if (!config)
            return null;

        var textarea = $(textAreaSelector).get(0) as any;
        if (textarea.value === undefined)
            textarea.value = '';

        const codeEditor = (window as any).CodeMirror.fromTextArea(textarea, config);
        (window as any).cmResize(codeEditor, {
            minWidth: 300,
            minHeight: 200,
            resizableWidth: true,
            resizableHeight: true
        });

        setTimeout(function() {
            // Prevent issue where you have to click on the text area for the text to appear.
            // https://github.com/codemirror/CodeMirror/issues/2469
            codeEditor.refresh();
        }, 1000);

        return codeEditor;
    }

    function getHtmlAndScriptConfig(): any {
        return {
            gutters: ['CodeMirror-lint-markers', 'CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
            lint: true,
            styleActiveLine: true,
            lineNumbers: true,
            lineWrapping: true,
            foldGutter: true,
            autoCloseTags: true,
            matchBrackets: true,
            autoCloseBrackets: true,
            continueComments: 'Enter',
            mode: {
                name: 'htmlmixed',
                scriptTypes: [
                    {
                    matches: /\/x-handlebars-template|\/x-mustache/i,
                    mode: null
                },
                {
                    matches: /(text|application)\/(x-)?vb(a|script)/i,
                    mode: 'vbscript'
                    }
                ]
            }
        };
    }

    function getScssConfig(): any {
        return {
            gutters: ['CodeMirror-lint-markers', 'CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
            lineNumbers: true,
            matchBrackets: true,
            autoCloseBrackets: true,
            mode: 'text/x-scss'
        };
    }

    function getCssConfig(): any {
        return {
            gutters: ['CodeMirror-lint-markers', 'CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
            lint: true,
            lineNumbers: true,
            matchBrackets: true,
            autoCloseBrackets: true,
            mode: 'text/x-gss'
        };
    }

    function getJsonConfig(): any {
        return {
            gutters: ['CodeMirror-lint-markers', 'CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
            lint: true,
            matchBrackets: true,
            autoCloseBrackets: true,
            mode: 'application/ld+json',
            lineWrapping: true,
            lineNumbers: true
        };
    }

    function getFormulaBuilderConfig(): any {
        return {
            lineNumbers: false,
            lineWrapping: true,
            matchBrackets: true,
            autoCloseBrackets: true,
            extraKeys: { Tab: 'autocomplete' },
            mode: { name: 'formula', globalVars: true },
            hintOptions: { completeSingle: false }
        };
    }

    export function setFormulaBuilderStructuredConfig(editor: any): void {
        editor.setOption('styleActiveLine', true);
        editor.setOption('autoCloseTags', true);
        editor.setOption('continueComments', 'Enter');
        editor.setOption('htmlMode', false);
        editor.setOption('hintOptions', { completeSingle: false });
        editor.setOption('mode', {
            name: 'formula',
            globalVars: true,
            scriptTypes: [
                {
                    matches: /\/x-handlebars-template|\/x-mustache/i,
                    mode: null
                },
                {
                    matches: /(text|application)\/(x-)?vb(a|script)/i,
                    mode: 'vbscript'
                }
            ]
        });
    }

    interface LintError {
        name: string;
        messages: Array<LintMessage>;
    }

    interface LintMessage {
        col: number;
        evidence: string;
        line: number;
        message: string;
        raw: string;
        type: string;
        rule: LintMessageRule;
    }

    interface LintMessageRule {
        id: string;
        description: string;
        link: string;
    }

    export enum codeTypes {
        htmlAndScript,
        scss,
        css,
        json,
        formulaBuilder
    }
}
